import React, { useState } from "react"
import { asDialogData } from "../../../core/utils/utils"
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, Grid, List, ListItem, ListItemText,
    MenuItem, Paper, Stack, TextField, Typography
} from "@mui/material"
import { format } from "date-fns"
import { getDateInServerTimeZone } from "../../../core/utils/utils"
import { useParams } from "react-router-dom"
import {
    useGetEmployeeExperienceQuery,
    useGetEmployeeExperienceTransactionsQuery,
    useGetAllExperienceCategoriesQuery,
    useGetEmployeeAchievementsQuery,
    useGetExperienceLevelsQuery,
    useGetMascotsQuery,
    useAdjustExperienceMutation,
    useRevokeAchievementMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"
import GrantAchievementDialog from "./GrantAchievementDialog"
import { MascotInfoCard } from "../../Experience/ExperiencePage"

function formatDate(s) {
    try { return format(getDateInServerTimeZone(s), "dd.MM.yyyy HH:mm") } catch { return s }
}

export default function EmployeeExperiencePage() {
    const { email: rawEmail } = useParams()
    const email = decodeURIComponent(rawEmail || "")
    const { data: snap, isLoading } = useGetEmployeeExperienceQuery(email)
    const { data: transactions = [] } = useGetEmployeeExperienceTransactionsQuery(email)
    const { data: categories = [] } = useGetAllExperienceCategoriesQuery()
    const { data: achievements = [] } = useGetEmployeeAchievementsQuery(email)
    const { data: levels = [] } = useGetExperienceLevelsQuery()
    const { data: mascots = [] } = useGetMascotsQuery()
    const [adjust, adjustResp] = useAdjustExperienceMutation()
    const [revoke, revokeResp] = useRevokeAchievementMutation()

    const [adjustOpen, setAdjustOpen] = useState(false)
    const [grantOpen, setGrantOpen] = useState(false)
    const [delta, setDelta] = useState("")
    const [categoryId, setCategoryId] = useState("")
    const [comment, setComment] = useState("")
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})

    async function submitAdjust() {
        const resp = await adjust({
            email,
            delta: parseInt(delta, 10) || 0,
            categoryId: categoryId || null,
            comment,
        })
        setActionResp(asDialogData(resp)); setRespOpen(true)
        if (!resp.error) {
            setAdjustOpen(false)
            setDelta(""); setCategoryId(""); setComment("")
        }
    }

    async function doRevoke(achievementId) {
        if (!window.confirm("Revoke this achievement?")) return
        const resp = await revoke({ achievementId, reason: "Revoked by admin" })
        setActionResp(asDialogData(resp)); setRespOpen(true)
    }

    if (isLoading || !snap) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>{email}</Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                        <Stack direction="row" spacing={4} alignItems="baseline">
                            <Box>
                                <Typography variant="caption" color="text.secondary">Total XP</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{snap.totalXp}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Level</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{snap.currentLevel}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Claimed</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{snap.lastClaimedLevel}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary">Pending BC</Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>{snap.pendingRewardBelcoins}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Button variant="outlined" onClick={() => setAdjustOpen(true)}>Adjust XP</Button>
                            <Button variant="outlined" onClick={() => setGrantOpen(true)}>Grant achievement</Button>
                        </Stack>
                    </Paper>

                    {/* Mascot + linked-category progress cards — same component the user sees. */}
                    {mascots.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: "var(--xp-orange)", textTransform: "uppercase", mb: 1.5 }}>
                                Squirrels and progress
                            </Typography>
                            <Grid container spacing={2}>
                                {mascots.map(m => {
                                    const cat = m.categoryId
                                        ? categories.find(c => c.id === m.categoryId)
                                        : null
                                    const xp = cat ? ((snap.categoryXp && snap.categoryXp[cat.name]) || 0) : 0
                                    return (
                                        <Grid item xs={12} sm={6} key={m.id}>
                                            <MascotInfoCard
                                                mascot={m}
                                                category={cat}
                                                xp={xp}
                                                levels={levels}
                                            />
                                        </Grid>
                                    )
                                })}
                            </Grid>
                        </Box>
                    )}

                    <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Transactions</Typography>
                        <List dense>
                            {transactions.map(tx => (
                                <ListItem key={tx.id} divider>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Chip size="small" label={tx.type} />
                                                {tx.categoryName && <Chip size="small" variant="outlined" label={tx.categoryName} />}
                                                <Typography component="span" sx={{ fontWeight: 700 }}>
                                                    {tx.delta > 0 ? `+${tx.delta}` : tx.delta} XP
                                                </Typography>
                                            </Stack>
                                        }
                                        secondary={`${tx.comment || ""} · ${formatDate(tx.createdAt)} · ${tx.authorName}`}
                                    />
                                </ListItem>
                            ))}
                            {transactions.length === 0 && <Typography color="text.secondary">No history</Typography>}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Achievements</Typography>
                        <List dense>
                            {achievements.map(a => (
                                <ListItem key={a.id} divider secondaryAction={
                                    <Button size="small" color="error" onClick={() => doRevoke(a.id)}>Revoke</Button>
                                }>
                                    <ListItemText
                                        primary={a.achievementCategoryName}
                                        secondary={`${a.grantReason} · ${formatDate(a.grantedAt)}`}
                                    />
                                </ListItem>
                            ))}
                            {achievements.length === 0 && <Typography color="text.secondary">No achievements</Typography>}
                        </List>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Adjust XP</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Delta XP (can be negative)"
                            type="number"
                            value={delta}
                            onChange={e => setDelta(e.target.value)}
                        />
                        <TextField
                            select
                            label="Category"
                            value={categoryId}
                            onChange={e => setCategoryId(e.target.value)}
                        >
                            <MenuItem value="">— not specified —</MenuItem>
                            {categories.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Comment"
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            multiline
                            minRows={2}
                            required
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAdjustOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={submitAdjust} disabled={adjustResp.isLoading}>
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <GrantAchievementDialog
                open={grantOpen}
                onClose={() => setGrantOpen(false)}
                defaultEmail={email}
            />

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="XP operation"
                information="Done"
            />
        </Box>
    )
}
